import { useEffect, useState } from "react";
import { Users, Shield, History, HeartHandshake } from "lucide-react";
import { useContent } from "../context/ContentContext";
import api from "../lib/api";
import HeroBackdrop from "../components/HeroBackdrop";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

function PersonCard({ p, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden flex items-center justify-center">
        {p.photo ? (
          <img src={resolve(p.photo)} alt={p.name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-20 w-20 rounded-full bg-[#0F2744] text-white text-xl font-bold flex items-center justify-center">{initials(p.name)}</div>
        )}
      </div>
      <div className="p-5">
        <div className="text-sm font-semibold text-amber-600">{p.role}</div>
        <div className="mt-0.5 font-bold text-[#0F2744]">{p.name}</div>
        {p.grade && <div className="mt-1 text-xs font-semibold text-slate-500">{p.grade}</div>}
        {p.bio && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.bio}</p>}
      </div>
    </div>
  );
}

export default function About() {
  const { t } = useContent();
  const [committee, setCommittee] = useState([]);
  useEffect(() => {
    api.get("/team", { params: { section: "committee" } }).then(({ data }) => setCommittee(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden bg-[#0F2744] py-16 lg:py-20">
        <HeroBackdrop sectionKey="about" image={t("img.about", "https://images.pexels.com/photos/28887409/pexels-photo-28887409.jpeg?auto=format&fit=crop&q=85&w=1600")} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">About us</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold text-white">A friendly club with a serious purpose</h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">RoSPA West Midlands Advanced Drivers and Riders has helped people raise their roadcraft since 1999.</p>
        </div>
      </section>

      {/* History */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-amber-100 flex items-center justify-center"><History className="h-6 w-6 text-amber-600" /></div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0F2744]">Our history</h2>
        </div>
        <p className="text-lg text-slate-600 leading-relaxed">
          {t("about.intro1", "The RoSPA West Midlands Group of Advanced Drivers and Riders has been around since 1999, though not always in its current form. It has a long-established reputation for helping riders and drivers improve their roadcraft skills and become safer on the road — while enjoying the pleasure of riding and driving.")}
        </p>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
          {t("about.intro2", "Our goal is to help any rider or driver, whatever their current ability, to raise their skills through our dedicated team of volunteer RoSPA-qualified advanced tutors, and achieve an advanced riding or driving test pass. The group is run by a voluntary committee elected at our AGM, and although we run separate sections for drivers and riders, we share one governing committee and membership.")}
        </p>
      </section>

      {/* Values */}
      <section className="bg-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Safety first", text: "Everything we do is rooted in reducing risk and building genuine skill." },
            { icon: HeartHandshake, title: "Friendship", text: "Group rides, club nights and weekends away with like-minded people." },
            { icon: Users, title: "Volunteer-led", text: "Our tutors and committee give their time as a voluntary contribution to help others progress." },
          ].map((v, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-7" data-testid={`value-${i}`}>
              <div className="h-11 w-11 rounded-xl bg-[#0F2744]/5 flex items-center justify-center mb-4"><v.icon className="h-6 w-6 text-[#0F2744]" /></div>
              <h3 className="text-lg font-bold text-[#0F2744]">{v.title}</h3>
              <p className="mt-2 text-slate-600">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Committee */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className="eyebrow">Who runs the club</span>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-[#0F2744] mb-8">Meet the committee</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="committee-grid">
          {committee.map((c, i) => <PersonCard key={c.id} p={c} testid={`committee-${i}`} />)}
        </div>
        <div className="mt-10 rounded-2xl bg-[#0F2744] text-white p-8">
          <h3 className="text-xl font-bold">Governance</h3>
          <p className="mt-3 text-slate-300 leading-relaxed">The club operates under a constitution and clear governance arrangements, with committee members elected at the AGM, and safeguarding and data protection taken seriously. Key governance documents are available to members in the member area.</p>
        </div>
      </section>
    </div>
  );
}
